import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import ThreeImagePhotoGallery from "./ThreeImagePhotoGallery";
import SixImagePhotoGallery from "./SixImagePhotoGallery";
import Expander from "./Expander";
import { renderKioskTapPdfPages, cancelKioskPdfRender } from './tapPdfJs';

function normalizeTapLinks(links) {
  if (!Array.isArray(links)) return [];
  return links
    .map((l) => ({
      target_page_id: Number(l?.target_page_id ?? l?.targetPageId),
      admin_title: String(l?.admin_title ?? l?.adminTitle ?? ''),
      x_pct: Number(l?.x_pct ?? l?.xPct ?? 0),
      y_pct: Number(l?.y_pct ?? l?.yPct ?? 0),
      width_pct: Number(l?.width_pct ?? l?.widthPct ?? 0),
      height_pct: Number(l?.height_pct ?? l?.heightPct ?? 0),
    }))
    .filter((l) => l.target_page_id > 0 && l.width_pct > 0 && l.height_pct > 0);
}

function TapPdfContent({ documentUrl, mediaType, tapLinks, onHotspotNavigate }) {
  const hostRef = useRef(null);
  const shellRef = useRef(null);

  const links = useMemo(() => normalizeTapLinks(tapLinks), [tapLinks]);

  useEffect(() => {
    if (mediaType !== 'pdf' || !documentUrl) return undefined;

    const host = hostRef.current;
    const shell = shellRef.current;
    if (!host || !shell) return undefined;

    let cancelled = false;
    let resizeTimer = null;

    const runRender = () => {
      if (cancelled) return;
      if (Math.floor(shell.getBoundingClientRect().width) < 8) return;
      cancelKioskPdfRender();
      renderKioskTapPdfPages(host, documentUrl);
    };

    const scheduleAfterLayout = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(runRender);
      });
    };

    scheduleAfterLayout();

    const onViewportResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(runRender, 280);
    };

    window.addEventListener('resize', onViewportResize);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', onViewportResize);
    }

    return () => {
      cancelled = true;
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', onViewportResize);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', onViewportResize);
      }
      cancelKioskPdfRender();
      host.innerHTML = '';
    };
  }, [mediaType, documentUrl]);

  const hotspotLayer = (
    <div
      className="tap-pdf-hotspots"
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        pointerEvents: 'none',
      }}
    >
      {links.map((link, i) => (
        <button
          key={`${link.target_page_id}-${i}`}
          type="button"
          className="tap-pdf-hotspot"
          style={{
            position: 'absolute',
            left: `${link.x_pct}%`,
            top: `${link.y_pct}%`,
            width: `${link.width_pct}%`,
            height: `${link.height_pct}%`,
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            minWidth: 8,
            minHeight: 8,
            pointerEvents: 'auto',
            touchAction: 'manipulation',
            WebkitTapHighlightColor: 'transparent',
          }}
          onClick={() => onHotspotNavigate(link.target_page_id)}
          aria-label={link.admin_title || 'Open linked page'}
        />
      ))}
    </div>
  );

  if (mediaType === 'pdf') {
    return (
      <div
        className="tap-pdf-stage tap-pdf-stage--pdf w-100 flex-grow-1"
        style={{
          flex: '1 1 0',
          minHeight: 0,
          minWidth: 0,
          overflow: 'auto',
          scrollbarGutter: 'stable',
          WebkitOverflowScrolling: 'touch',
          overscrollBehavior: 'contain',
          touchAction: 'pan-y',
          background: '#fff',
        }}
      >
        <div ref={shellRef} className="position-relative w-100 bg-white">
          <div ref={hostRef} className="tap-pdf-js-host w-100" />
          {hotspotLayer}
        </div>
      </div>
    );
  }

  return (
    <div
      className="tap-pdf-stage tap-pdf-stage--image position-relative w-100 flex-grow-1"
      style={{
        flex: '1 1 0',
        minHeight: 0,
        overflow: 'auto',
        overscrollBehavior: 'contain',
        background: '#fff',
      }}
    >
      <div ref={shellRef} className="position-relative w-100">
        <img
          src={documentUrl}
          alt=""
          className="w-100 d-block"
          style={{ pointerEvents: 'none', userSelect: 'none', verticalAlign: 'top' }}
        />
        {hotspotLayer}
      </div>
    </div>
  );
}

export default function TouchScreen() {
  const [menuData, setMenuData] = useState(null);
  const [modalData, setModalData] = useState(null);
  const [timeoutDuration, setTimeoutDuration] = useState(30000); // default 30 seconds
  const [open, setOpen] = useState(false);
  const carouselRef = useRef(null);
  const timeoutRef = useRef(null);

  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();


  // -----------------------------
  // Fetch Menu Data
  // -----------------------------
  const fetchMenu = async () => {
    try {
      const res = await Backend.get(`/touch_screen_modules/${id}`, { params: { dsm_id: new URLSearchParams(location.search).get('dsm_id') } });
      const data = res?.data || res || {};

      setMenuData(data);

      // Set timeout dynamically (convert seconds → milliseconds)
      if (data?.timeout_duration) {
        setTimeoutDuration(data.timeout_duration * 1000);
        console.log('Timeout from timeout_duration:', data.timeout_duration * 1000);
      } else if (data?.timeout) {
        setTimeoutDuration(data.timeout * 1000);
        console.log('Timeout from timeout:', data.timeout * 1000);
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
    }
  };

  // -----------------------------
  // Handle Menu Image Click
  // -----------------------------
  const handleImageClick = async (menuId) => {
    try {
      resetTimeout(); // refresh inactivity timer

      const res = await Backend.get(`/touch_screen_modules/${id}/content/${menuId}`);
      const data = res?.data || res || {};

      setModalData(data);
      setOpen(true);
    } catch (error) {
      console.error('Error loading modal data:', error);
    }
  };

  // -----------------------------
  // Carousel Scrolling
  // -----------------------------
  const scrollCarousel = (direction) => {
    if (carouselRef?.current) {
      const scrollAmount = carouselRef.current.offsetWidth / 2;
      carouselRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
    resetTimeout();
  };

  // -----------------------------
  // Timeout + Redirect
  // -----------------------------
  const resetTimeout = useCallback(() => {
    if (timeoutRef?.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      // redirect back to large screen
      if (location?.state?.from) {
        history.replace(location.state.from, {});
      }
    }, timeoutDuration);
  }, [timeoutDuration, history, location?.state]);

  // -----------------------------
  // Setup Effects
  // -----------------------------
  useEffect(() => {
    fetchMenu();

    const handleActivity = () => resetTimeout();

    // Add listeners for user activity
    window.addEventListener('click', handleActivity);
    window.addEventListener('touchstart', handleActivity);
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);

    resetTimeout(); // start initial timer

    return () => {
      clearTimeout(timeoutRef?.current);
      window.removeEventListener('click', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
    };
  }, [id, location.search, resetTimeout]);

  if (!menuData) return <p>Loading...</p>;

  const { type, images = [], tap_pdf: tapPdf } = menuData || {};
  const logoImage = images?.find?.((img) => img?.type === 'logo');

  const navigateToTapPdfTarget = (targetPageId) => {
    resetTimeout();
    const q = location.search || '';
    history.push(`/touch-screen/${targetPageId}${q}`);
  };

  const renderTapPdf = () => {
    if (!tapPdf?.document_url) {
      return <p className="p-4 text-muted">No document uploaded for this Tap PDF page.</p>;
    }

    const { document_url: documentUrl, media_type: mediaType, tap_links: tapLinks = [] } = tapPdf;
    const pdfOrImage = mediaType === 'pdf' ? 'pdf' : 'image';

    return (
      <TapPdfContent
        documentUrl={documentUrl}
        mediaType={pdfOrImage}
        tapLinks={tapLinks}
        onHotspotNavigate={navigateToTapPdfTarget}
      />
    );
  };

  // -----------------------------
  // Render Menu Layouts
  // -----------------------------
  const renderTwoWide = () => (
    <div className="menu two-wide">
      {images?.slice?.(1, 3)?.map?.((menu) => (
        <div key={menu?.id} className="menu-item" onClick={() => handleImageClick(menu?.id - 1)}>
          <img src={menu?.url} alt={menu?.text || ''} />
          <p className="menu-text-with-arrow">
            <span className="menu-text">{menu?.text}</span>
            {menu?.text && <span className="arrow-icon"><svg width="36" height="30" viewBox="0 0 36 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M34.8204 15.7275L21.6016 28.9462C21.326 29.2218 20.9523 29.3766 20.5625 29.3766C20.1727 29.3766 19.799 29.2218 19.5234 28.9462C19.2478 28.6706 19.0929 28.2968 19.0929 27.9071C19.0929 27.5173 19.2478 27.1435 19.5234 26.8679L30.2361 16.1571H1.46875C1.07921 16.1571 0.705631 16.0023 0.430187 15.7269C0.154743 15.4514 0 15.0779 0 14.6883C0 14.2988 0.154743 13.9252 0.430187 13.6498C0.705631 13.3743 1.07921 13.2196 1.46875 13.2196H30.2361L19.5234 2.50871C19.2478 2.23311 19.0929 1.85932 19.0929 1.46957C19.0929 1.07981 19.2478 0.706023 19.5234 0.430426C19.799 0.154829 20.1727 0 20.5625 0C20.9523 0 21.326 0.154829 21.6016 0.430426L34.8204 13.6492C34.957 13.7856 35.0653 13.9476 35.1392 14.1259C35.2131 14.3042 35.2512 14.4953 35.2512 14.6883C35.2512 14.8813 35.2131 15.0725 35.1392 15.2508C35.0653 15.4291 34.957 15.5911 34.8204 15.7275Z" fill="#00A7FF"/></svg></span>}
          </p>
        </div>
      ))}
    </div>
  );

  const renderThreeWide = () => (
    <div className="menu three-wide">
      {images?.slice?.(1, 4)?.map?.((menu) => (
        <div key={menu?.id} className="menu-item" onClick={() => handleImageClick(menu?.id - 1)}>
          <img src={menu?.url} alt={menu?.text || ''} />
          <p className="menu-text-with-arrow">
            <span className="menu-text">{menu?.text}</span>
            {menu?.text && <span className="arrow-icon"><svg width="36" height="30" viewBox="0 0 36 30" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M34.8204 15.7275L21.6016 28.9462C21.326 29.2218 20.9523 29.3766 20.5625 29.3766C20.1727 29.3766 19.799 29.2218 19.5234 28.9462C19.2478 28.6706 19.0929 28.2968 19.0929 27.9071C19.0929 27.5173 19.2478 27.1435 19.5234 26.8679L30.2361 16.1571H1.46875C1.07921 16.1571 0.705631 16.0023 0.430187 15.7269C0.154743 15.4514 0 15.0779 0 14.6883C0 14.2988 0.154743 13.9252 0.430187 13.6498C0.705631 13.3743 1.07921 13.2196 1.46875 13.2196H30.2361L19.5234 2.50871C19.2478 2.23311 19.0929 1.85932 19.0929 1.46957C19.0929 1.07981 19.2478 0.706023 19.5234 0.430426C19.799 0.154829 20.1727 0 20.5625 0C20.9523 0 21.326 0.154829 21.6016 0.430426L34.8204 13.6492C34.957 13.7856 35.0653 13.9476 35.1392 14.1259C35.2131 14.3042 35.2512 14.4953 35.2512 14.6883C35.2512 14.8813 35.2131 15.0725 35.1392 15.2508C35.0653 15.4291 34.957 15.5911 34.8204 15.7275Z" fill="#00A7FF"/></svg></span>}
          </p>
        </div>
      ))}
    </div>
  );

  const renderCarousel = () => (
    <div className="carousel-wrapper">
      {images?.length > 4 && (
        <button className="scroll-btn left" onClick={() => scrollCarousel('left')}>
          &#8249;
        </button>
      )}
      <div className="menu touch-carousel" ref={carouselRef}>
        {images?.slice?.(1, 31)?.map?.((menu) => (
          <div
            key={menu?.id}
            className="menu-item touch-carousel-item"
            onClick={() => handleImageClick(menu?.id - 1)}
          >
            <img src={menu?.url} alt={menu?.text || ''} />
            <p>{menu?.text}</p>
          </div>
        ))}
      </div>
      {images?.length > 4 && (
        <button className="scroll-btn right" onClick={() => scrollCarousel('right')}>
          &#8250;
        </button>
      )}
    </div>
  );

  // -----------------------------
  // Render Modal Content Dynamically
  // -----------------------------
  const renderModalContent = () => {
    if (!modalData) return null;

    switch (modalData?.content_type) {
      case 'three-image':
        return (
          <div className="p-3">
            <h2 className="top-header">{modalData?.top_header}</h2>
            <div className="header-divider"></div>

            <div className="text-section my-4">
              <div className="text-spacer"></div>
              <div
                className="description-text"
                dangerouslySetInnerHTML={{ __html: modalData?.top_subtext || '' }}
              />
            </div>

            <div className="layout-header second-header">
              <h3 className="approach-label">{modalData?.lower_header}</h3>
              <div className="header-divider"></div>
            </div>

            <div className="text-section my-3">
              <div className="text-spacer"></div>
              <div
                className="description-text"
                dangerouslySetInnerHTML={{ __html: modalData?.lower_subtext || '' }}
              />
            </div>

           <div className="images-grid-section">
              {modalData?.content_images?.[0]?.url && (
                <div className="image-container first-image">
                  <img src={modalData?.content_images?.[0]?.url} alt="image one" />
                </div>
              )}
              {modalData?.content_images?.[1]?.url && (
                <div className="image-container second-image">
                  <img src={modalData?.content_images?.[1]?.url} alt="image two" />
                </div>
              )}
              {modalData?.content_images?.[2]?.url && (
                <div className="image-container third-image">
                  <img src={modalData?.content_images?.[2]?.url} alt="image three" />
                </div>
              )}
            </div>
          </div>
        );

      case 'four-image':
        return (
          <div className="p-3">
            <h2 className="top-header">{modalData?.top_header}</h2>
            <div className="header-divider"></div>

            <div className="text-section my-4">
              <h3 className="approach-label">{modalData?.secondary_header}</h3>
              <p className="description-text m-0" dangerouslySetInnerHTML={{ __html: modalData?.lower_subtext || '' }}></p>
            </div>

            {modalData?.content_images?.[0]?.url && (
              <div className="image-container first-image"  style={{ width: "100%", height: "400px", border: "none", marginBottom: "0.35rem" }} allowFullScreen>
                <img src={modalData?.content_images?.[0]?.url} alt="image one"/>
              </div>
            )}

            <div className="images-grid-section">
              
              {modalData?.content_images?.[1]?.url && (
                <div className="image-container second-image">
                  <img src={modalData?.content_images?.[1]?.url} alt="image two" />
                </div>
              )}
              {modalData?.content_images?.[2]?.url && (
                <div className="image-container third-image">
                  <img src={modalData?.content_images?.[2]?.url} alt="image three" />
                </div>
              )}
              {modalData?.content_images?.[3]?.url && (
                <div className="image-container four-image">
                  <img src={modalData?.content_images?.[3]?.url} alt="image four" />
                </div>
              )}
            </div>
          </div>
        );

      case 'web':
        return (
          <div className="p-3">
            <h2 className="top-header">{modalData?.top_header}</h2>
            <div className="header-divider"></div>

            <div className="text-section my-4">
              <div className="text-spacer"></div>
              <p className="description-text" dangerouslySetInnerHTML={{ __html: modalData?.lower_subtext || '' }}></p>
            </div>

            {modalData?.iframe_url && (
              <iframe
                src={modalData?.iframe_url}
                title="Content iframe"
                style={{ width: "100%", height: "400px", border: "none" }}
                allowFullScreen
              />
            )}
          </div>
        );

      case 'expander':
        return (
          <div className="expander-gallery">
            <Expander data={modalData} />
          </div>
        );

      case 'photo-gallery-six':
        return (
          <div className="gallery six-image">
            <SixImagePhotoGallery data={modalData} />
          </div>
        );

      case 'photo-gallery-three':
        return (
          <div className="gallery three-image">
            <ThreeImagePhotoGallery data={modalData} />
          </div>
        );
      default:
        return <p className="description-text">No content available.</p>;
    }
  };

  // -----------------------------
  // Render
  // -----------------------------
  const containerClass =
    type === 'tap-pdf'
      ? `touchscreen-container tap-pdf d-flex flex-column ${type || ''}`
      : `touchscreen-container ${type || ''}`;

  const containerStyle =
    type === 'tap-pdf'
      ? {
          minHeight: '100vh',
          height: '100dvh',
          maxHeight: '100dvh',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          padding: 0,
          margin: 0,
          overflow: 'hidden',
        }
      : undefined;

  return (
    <div className={containerClass} style={containerStyle}>
      {logoImage && (
        <div className="logo-container flex-shrink-0">
          <img src={logoImage?.url} alt="Logo" className="menu-logo" />
        </div>
      )}

      {type === 'tap-pdf' && renderTapPdf()}
      {type === 'two-wide' && renderTwoWide()}
      {type === 'three-wide' && renderThreeWide()}
      {type === 'carousel' && renderCarousel()}

      <Modal show={open} onHide={() => setOpen(false)} centered size="xl">
        <Modal.Body>{renderModalContent()}</Modal.Body>
      </Modal>
    </div>
  );
}
